// A workspace's dock (WorkspaceDock), lent to the board in front: the tabs
// that board adds beside the chat (the cell picked on it), the one it has in
// front, and where in the dock its pane is drawn
// (`slot`). The board lends its tabs while it is in front and draws its pane
// into the slot; the dock picks or closes a tab through it. `ask` counts the
// times a board asked for its pane to be shown (a cell picked on it), so the
// dock opens on it then, and not whenever a board comes in front.
//
// There is one such dock for each place a workspace is drawn (`scope`): the
// workspace home's, beside its panes ("home"), and the one of a workspace
// over the page ("overlay": a session previewed over the home's panes, or a
// workspace over another page). A board lends the dock of the scope it is
// drawn in (DockScope, given by its dock). The docks up stand in the order
// they came: the last is in front, and what asks for a dock from outside
// one (a chat handed something: askDockTab) asks that one.
import { createContext, useContext, useSyncExternalStore } from "react";

// `tabAsk`: a tab asked to be brought in front from outside the dock ({ tab,
// n }, `n` counting asks): a chat that was handed something to talk about.
const BLANK = Object.freeze({ owner: null, tabs: [], active: null, ask: 0, slot: null, tabAsk: { tab: null, n: 0 } });
const states = new Map();
// A board not yet in front that asked (a cell picked in a pane the click
// also brings in front), by scope: its ask counts once it lends its tabs.
const pending = new Map();
const handlers = new Map();
const up = [];
const subs = new Set();
const emit = () => { for (const fn of [...subs]) fn(); };

export const DockScope = createContext("home");
export const useDockScope = () => useContext(DockScope);

const stateOf = (scope) => states.get(scope) || BLANK;
const put = (scope, next) => {
  states.set(scope, next);
  emit();
};
const scopeOf = (owner) => owner?.scope || "home";

// The dock in front: the last one up, else the home's.
export function frontDockScope() {
  return up.length ? up[up.length - 1] : "home";
}

// A dock comes up (`scope`), in front of those up before it; the returned
// call puts it away.
export function dockUp(scope) {
  up.push(scope);
  emit();
  return () => {
    const at = up.lastIndexOf(scope);
    if (at >= 0) up.splice(at, 1);
    emit();
  };
}

// Lent again with nothing changed (the board drawn again around it), the
// dock is left alone: only a different tab, label or tab in front draws it
// again.
const sameTabs = (a, b) => a.length === b.length && a.every((t, i) => t.id === b[i].id && t.label === b[i].label
  && (t.dot || null) === (b[i].dot || null) && !!t.closable === !!b[i].closable);

// `owner`: the board, its `scope` the dock it lends.
export function lendDock(owner, { tabs = [], active = null, onSelect = null, onClose = null } = {}) {
  const scope = scopeOf(owner);
  const state = stateOf(scope);
  handlers.set(owner, { onSelect, onClose });
  const asked = pending.get(scope) === owner;
  if (state.owner !== owner) pending.delete(scope);
  if (state.owner === owner && state.active === active && sameTabs(state.tabs, tabs)) return;
  put(scope, { ...state, owner, tabs, active, ask: asked ? state.ask + 1 : state.ask });
}

// `tab`: the one the board is bringing in front as it asks, so the dock reads
// the ask and the tab in front together; the board lends its tabs only as it
// draws again, after it asked.
export function askDock(owner, tab = null) {
  const scope = scopeOf(owner);
  const state = stateOf(scope);
  if (state.owner !== owner) {
    pending.set(scope, owner);
    return;
  }
  put(scope, { ...state, ask: state.ask + 1, active: tab ?? state.active });
}

export function takeDockBack(owner) {
  const scope = scopeOf(owner);
  const state = stateOf(scope);
  handlers.delete(owner);
  if (pending.get(scope) === owner) pending.delete(scope);
  if (state.owner !== owner) return;
  put(scope, { ...state, owner: null, tabs: [], active: null });
}

export function pickDockTab(id, scope = "home") {
  handlers.get(stateOf(scope).owner)?.onSelect?.(id);
}

export function closeDockTab(id, scope = "home") {
  handlers.get(stateOf(scope).owner)?.onClose?.(id);
}

export function setDockSlot(el, scope = "home") {
  const state = stateOf(scope);
  if (state.slot === (el || null)) return;
  put(scope, { ...state, slot: el || null });
}

// Brings a main or board tab in front. A canvas target keeps requests from
// acting on another workspace if the board changes before they are handled.
export function askDockTab(tab, scope = frontDockScope(), canvasId = null) {
  if (!tab) return;
  const state = stateOf(scope);
  put(scope, { ...state, tabAsk: { tab, n: state.tabAsk.n + 1, ...(canvasId ? { canvasId } : {}) } });
}

// A dock's state: the one in front's, unless another is named.
export function dockBridge(scope = frontDockScope()) {
  return stateOf(scope);
}

export function subscribeDockBridge(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

// The dock of the scope a component is drawn in, or of the one named.
export function useDockBridge(scope = null) {
  const given = useDockScope();
  const at = scope || given;
  return useSyncExternalStore(subscribeDockBridge, () => stateOf(at), () => stateOf(at));
}
